import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../constants";
import CodesInUseBar from "./CodesInUseBar";
import ContactsTable from "./ContactsTable";
import UsersTable from "./UsersTable";
import PaymentsTable from "./PaymentsTable";
import RefundActivityCard from "./RefundActivityCard";

const SUB_TABS = ["contacts", "users", "payments"];

/**
 * DashboardTab — top-level CRM dashboard.
 * Loads stats + submissions + users + payments and renders the active sub-table.
 * Admin actions (grant/refund lifetime, delete submission, CSV export) live here
 * because they share the same `load()` refresh.
 */
export default function DashboardTab({ adminPassword }) {
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [dashTab, setDashTab] = useState("contacts");
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [statsRes, contactsRes, usersRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/crm/dashboard-stats`),
        axios.get(`${API}/crm/contact-submissions`),
        axios.get(`${API}/crm/users`),
        axios.get(`${API}/crm/payments`),
      ]);
      setStats(statsRes.data);
      setSubmissions(contactsRes.data.items);
      setUsers(usersRes.data.items);
      setPayments(paymentsRes.data.items);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markLifetime = async (email) => {
    if (!window.confirm(`Grant lifetime to ${email}? This is idempotent.`)) return;
    try {
      await axios.post(`${API}/admin/grant-lifetime`, { password: adminPassword, email });
      toast.success(`${email} → lifetime`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark lifetime");
    }
  };

  const refundLifetime = async (email) => {
    const reason = window.prompt(`Refund lifetime for ${email}? Enter reason (chargeback / refund / fraud / etc.):`);
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/refund-lifetime`, { password: adminPassword, email, reason });
      toast.success(`${email} → refunded / expired`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to refund");
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/crm/contact-submissions/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const exportToCSV = () => {
    const data = dashTab === "contacts" ? submissions : dashTab === "users" ? users : payments;
    if (!data.length) return;
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map((item) => Object.values(item).map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([`${headers}\n${rows}`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${dashTab}_export.csv`;
    a.click();
  };

  return (
    <div data-testid="crm-dashboard">
      <CodesInUseBar />

      <RefundActivityCard adminPassword={adminPassword} />

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="crm-stats">
          <div className="card-glass p-6"><p className="text-[#E8E4DC] mb-2">Total Submissions</p><p className="text-4xl font-bold text-[#A3E635]">{stats.total_contacts}</p></div>
          <div className="card-glass p-6"><p className="text-[#E8E4DC] mb-2">Total Users</p><p className="text-4xl font-bold text-[#A3E635]">{stats.total_users}</p></div>
          <div className="card-glass p-6"><p className="text-[#E8E4DC] mb-2">Paid Subscriptions</p><p className="text-4xl font-bold text-[#A3E635]">{stats.total_payments}</p></div>
        </div>
      )}

      <div className="flex gap-4 mb-6">
        {SUB_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setDashTab(tab)}
            className={`px-4 py-2 rounded-lg transition-colors capitalize text-sm font-medium ${dashTab === tab ? "bg-[#A3E635] text-black" : "text-[#E8E4DC] hover:text-white"}`}
            data-testid={`dash-tab-${tab}`}
          >
            {tab}
          </button>
        ))}
        <button onClick={exportToCSV} className="ml-auto btn-secondary text-sm" data-testid="crm-export">
          Export CSV
        </button>
      </div>

      <div className="card-glass overflow-hidden" data-testid="crm-table">
        {isLoading ? (
          <div className="p-8 text-center text-[#E8E4DC]">Loading...</div>
        ) : dashTab === "contacts" ? (
          <ContactsTable submissions={submissions} onDelete={deleteSubmission} />
        ) : dashTab === "users" ? (
          <UsersTable users={users} onMarkLifetime={markLifetime} onRefundLifetime={refundLifetime} />
        ) : (
          <PaymentsTable payments={payments} />
        )}
      </div>
    </div>
  );
}
